import { motion } from "framer-motion";
import { cadence } from "@/data/showcase";
import { fadeUp, staggerSlow, viewportOnce } from "@/lib/motion";

const { eyebrow, titleStart, titleItalic, body, days } = cadence;

export function Cadence() {
  return (
    <section className="relative bg-[#06080F] border-t border-ink-900 py-16 md:py-24 overflow-hidden">
      <div className="relative mx-auto max-w-[1080px] px-6">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mb-12 md:mb-16 max-w-[640px]"
        >
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-ink-500 mb-3">
            {eyebrow}
          </p>
          <h2 className="font-serif text-white leading-[1.05] text-3xl md:text-4xl lg:text-5xl">
            {titleStart}{" "}
            <span className="text-foton-lighter italic font-display">{titleItalic}</span>
          </h2>
        </motion.div>

        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="text-base md:text-lg text-ink-300 leading-relaxed max-w-[600px] mb-14"
        >
          {body}
        </motion.p>

        {/* Week as a vertical journal — hairline rules, no cards.
            Asymmetric: long day name | optional time | event description. */}
        <motion.dl
          variants={staggerSlow}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="max-w-[680px]"
        >
          {days.map((day) => {
            const hasEvent = day.events.length > 0;
            const event = day.events[0];
            return (
              <motion.div
                key={day.d}
                variants={fadeUp}
                className="relative grid grid-cols-[1fr_auto] md:grid-cols-[180px_70px_1fr] gap-x-4 md:gap-x-8 gap-y-1 items-baseline py-5 border-b border-ink-800 last:border-b-0"
              >
                {hasEvent && (
                  <span
                    aria-hidden
                    className="absolute -left-3 top-7 w-1.5 h-1.5 rounded-full bg-foton-light"
                    style={{ boxShadow: "0 0 8px #8D51FF" }}
                  />
                )}
                <dt
                  className={
                    "font-serif text-xl md:text-3xl " +
                    (hasEvent ? "text-white" : "text-ink-700")
                  }
                >
                  {DAY_NAMES[day.d] ?? day.d}
                </dt>
                <span
                  className={
                    "font-mono text-[11px] tabular-nums shrink-0 " +
                    (hasEvent ? "text-foton-lighter" : "text-ink-700")
                  }
                >
                  {event?.time ?? "—"}
                </span>
                <dd
                  className={
                    "col-span-2 md:col-span-1 text-sm md:text-base " +
                    (hasEvent ? "text-ink-200" : "text-ink-600 italic")
                  }
                >
                  {event?.text ?? day.fillerLabel ?? ""}
                </dd>
              </motion.div>
            );
          })}
        </motion.dl>
      </div>
    </section>
  );
}

const DAY_NAMES: Record<string, string> = {
  Pn: "Poniedziałek",
  Wt: "Wtorek",
  Śr: "Środa",
  Cz: "Czwartek",
  Pt: "Piątek",
  Sb: "Sobota",
  Nd: "Niedziela",
};
